import { guid, iCaseCompare } from '@fry/lib/utils';

import { EditorBookingItemMetadata } from '../editor-booking-item-metadata/editor-booking-item-metadata.model';
import { EditorBookingItemSetting } from '../editor-booking-item-setting/editor-booking-item-setting.model';


export const SETTING_BOOKING_DATE_INDEX = 0;
export const SETTING_PRICING_INDEX = 1;
export const SETTING_CAPACITY_INDEX = 2;
export const SETTING_VENUE_INDEX = 3;
export const SETTING_BOOKING_WINDOW_INDEX = 4;
export const SETTING_WORKFLOW_INDEX = 5;
export const SETTING_ELIGIBILITY_INDEX = 6;


export class EditorBookingItem {
  id: string;
  parentId: string;
  name: string;
  canBeBooked: boolean;
  ancestorCanBeBooked: boolean;
  hideSpecificBookingSettings: boolean;
  level: number;
  isLastSibling: boolean;
  children: string[];
  siblings: string[];
  bundle: string[];
  settings: EditorBookingItemSetting[];
  metadataList: EditorBookingItemMetadata[];
  showSettings: boolean;

  constructor(obj?: any) {
    this.id = obj?.id ?? null;
    this.parentId = obj?.parentId ?? null;
    this.name = obj?.name ?? '';
    this.canBeBooked = obj?.canBeBooked ?? false;
    this.ancestorCanBeBooked = obj?.ancestorCanBeBooked ?? false;
    this.hideSpecificBookingSettings = obj?.hideSpecificBookingSettings ?? false;
    this.level = obj?.level ?? 0;
    this.isLastSibling = obj?.isLastSibling ?? false;
    this.children = obj?.children ?? [];
    this.siblings = obj?.siblings ?? [];
    this.bundle = obj?.bundle ?? [];
    this.settings = obj?.settings ?? [];
    this.metadataList = obj?.metadataList ?? [];
    this.showSettings = obj?.showSettings ?? false;
  }

  static mapMinimalSettings(settings: EditorBookingItemSetting[]): EditorBookingItemSetting[] {
    return settings.map(
      setting => setting.inheritableType === 'set' || setting.inheritableType === 'overridden'
        ? setting
        : undefined
    );
  }

  static filterMinimalMetadataList(metadataList: EditorBookingItemMetadata[]): EditorBookingItemMetadata[] {
    return metadataList.filter(metadata =>
      metadata.inheritableType === 'set' || metadata.inheritableType === 'overridden'
    );
  }

  createCopy(): EditorBookingItem {
    return new EditorBookingItem({
      id: this.id,
      parentId: this.parentId,
      name: this.name,
      canBeBooked: this.canBeBooked,
      ancestorCanBeBooked: this.ancestorCanBeBooked,
      hideSpecificBookingSettings: this.hideSpecificBookingSettings,
      level: this.level,
      isLastSibling: this.isLastSibling,
      bundle: [...this.bundle],
      settings: this.settings.map(itemSetting => itemSetting.createCopy()),
      metadataList: this.metadataList.map(metadata => metadata.createCopy()),
      showSettings: this.showSettings
    });
  }

  createClone(): EditorBookingItem {
    const clonedItem = this.createCopy();
    clonedItem.id = guid();
    return clonedItem;
  }

  createChild(): EditorBookingItem {
    return new EditorBookingItem({
      id: guid(),
      parentId: this.id,
      ancestorCanBeBooked: this.ancestorCanBeBooked || this.canBeBooked,
      hideSpecificBookingSettings: this.hideSpecificBookingSettings,
      level: this.level + 1,
      settings: this.getCompleteSettings([]),
      metadataList: this.getCompleteMetadataList([])
    });
  }

  immutableSetSiblingsFromChildren(children: string[]): EditorBookingItem {
    return Object.assign(
      new EditorBookingItem(),
      this,
      {
        siblings: children.filter(childItemId => childItemId !== this.id)
      }
    );
  }

  immutableRestrictBundleToSiblings(siblings: string[]): EditorBookingItem {
    return Object.assign(
      new EditorBookingItem(),
      this,
      {
        bundle: this.bundle.filter(bundleItemId => siblings.includes(bundleItemId))
      }
    );
  }

  getCompleteSettings(partialSettings: EditorBookingItemSetting[]): EditorBookingItemSetting[] {
    const minimalSettings = EditorBookingItem.mapMinimalSettings(partialSettings);

    return this.settings.map((thisSetting, thisSettingIndex) => {
      const setting = minimalSettings[thisSettingIndex];

      if (setting === undefined) {
        return thisSetting.createChild();
      } else {
        return setting.createComplete(thisSetting);
      }
    });
  }

  getCompleteMetadataList(partialMetadataList: EditorBookingItemMetadata[]): EditorBookingItemMetadata[] {
    let minimalMetadataList = EditorBookingItem.filterMinimalMetadataList(partialMetadataList);

    const notInMinimalMetadataList = this.metadataList.filter(metadata =>
      !minimalMetadataList.some(minimalMetadata => minimalMetadata.name === metadata.name)
    ).map(metadata => metadata.createChild());

    minimalMetadataList = minimalMetadataList.map(minimalMetadata => {
      const thisMetadata = this.metadataList.find(metadata => metadata.name === minimalMetadata.name);
      if (thisMetadata === undefined) {
        return new EditorBookingItemMetadata({ ...minimalMetadata, inheritableType: 'set' });
      } else {
        return minimalMetadata.createComplete(thisMetadata);
      }
    });

    return notInMinimalMetadataList.concat(minimalMetadataList).sort(
      (a, b) => iCaseCompare(a.name, b.name)
    );
  }

  canBeBookedIsEditable(): boolean {
    return !this.ancestorCanBeBooked && this.children.length > 0;
  }

  userCanBook(): boolean {
    return !this.ancestorCanBeBooked && (this.canBeBooked || this.children.length === 0);
  }

  hideSpecificBookingSettingsIsEditable(): boolean {
    return this.userCanBook() && this.children.length > 0;
  }

  bundleIsEditable(): boolean {
    return this.siblings.length > 0;
  }

  getSettingsWithSummary() {
    return this.settings.filter(setting => setting.showSummary && setting.hasSummary());
  }
}
